/*
 * Developer  : Sakshi Bhise
 * Script     : SidebarNavItems.tsx
 * Date       : 2026-06-08
 * Purpose    : Renders the navigation icons in the main sidebar body.
 *              Shows a Chat History icon (and, for support staff, a Support Mode
 *              icon) and displays a mini list of the 5 most recent conversations
 *              beneath the Chat History icon when the sidebar is expanded.
 */

// ─── Imports ──────────────────────────────────────────────────────────────────

import { History, Ticket as TicketIcon } from "lucide-react";         // History icon for Chat History; Ticket icon for Create HR Ticket
import { FiUserCheck, FiUserX } from "react-icons/fi";               // User icons: green tick when impersonation is active, red cross when it is not
import { useTheme, mergeStyles } from "@fluentui/react";              // useTheme reads the active colour theme; mergeStyles combines CSS class names
import { RecentConversationsSection, useSidebar } from "@teva/ui";   // RecentConversationsSection shows a mini list of recent chats; useSidebar reads whether the sidebar is expanded
import { getSidebarLayoutStyles } from "./Sidebarlayout.style";       // CSS-in-JS styles shared across sidebar elements
import { useTranslation } from "react-i18next";                       // Hook that returns translated text for the currently active language
import React, { useContext, useMemo } from "react";         // React core; useMemo caches computed values so they are not recalculated on every render
import { AppStateContext } from "../../state/AppProvider";            // Global app state — provides the full chat history list
import { Conversation } from "../../api/models";                      // TypeScript type describing what a single conversation object looks like
import i18n from "../../i18n";                                        // Internationalisation engine — used here to detect right-to-left languages (Hebrew, Arabic)
import { historyDelete, historyRename } from "../../api";
import { AuthContext } from "../../state/AuthContext";
import { toast } from "react-toastify";

// ─── Constants ────────────────────────────────────────────────────────────────

// The default navigation items shown in the sidebar for every user.
// Each entry has: an id (used to identify which panel to open), an icon, a
// translation key for the label, and a flag indicating it opens a side panel.
export const navItems = [
    { id: 'create-ticket', icon: <TicketIcon size={18} />, label: "chat.openTicket", openPanel: false },
    { id: 'chat-history', icon: <History size={18} />, label: "chatHistoryPanel.title", openPanel: true },
];

// ─── Types & Interfaces ───────────────────────────────────────────────────────

// Describes the settings passed into SidebarNavItems by its parent component
interface SidebarNavItemsProps {
    activePanel: string | null;           // the id of the panel currently open (e.g. 'chat-history'), or null if none is open
    onNavItemClick: (id: string) => void; // called when the user clicks a nav item — receives the item's id so the parent knows what to open
    highlightActive?: boolean;            // when true, the icon for the open panel is highlighted; defaults to true
    isSupportUser?: boolean;              // when true, an extra Support Mode item is added at the top of the list; defaults to false
    impersonationActive?: boolean;        // when true the Support Mode icon shows a green tick; when false a red cross; defaults to false
}

// ─── Component ────────────────────────────────────────────────────────────────

// Renders all navigation icons in the sidebar body.
// Support users see an extra "Support Mode" item above the standard items.
// When the sidebar is expanded, the Chat History item shows a mini list of the
// 5 most recent conversations directly beneath it.
export function SidebarNavItems({
    activePanel,
    onNavItemClick,
    highlightActive = true,
    isSupportUser = false,
    impersonationActive = false,
}: SidebarNavItemsProps) {
    const theme = useTheme();                       // active colour theme — used to colour active/hover states
    const styles = getSidebarLayoutStyles(theme);   // CSS-in-JS rules for menu item layout and colours
    const { expanded } = useSidebar();              // true when the sidebar is wide and showing labels alongside icons
    const { t } = useTranslation();                 // returns translated text for the active language
    const appStateContext = useContext(AppStateContext); // global state — chat history is read from here
    const { token } = useContext(AuthContext);
    const country = appStateContext?.state.loggedInUserCountry;
    const globalId = appStateContext?.state.userGId;
    const chatHistory = appStateContext?.state.chatHistory ?? [];

    // Builds the 5 most recent conversations to show under the Chat History icon.
    // Recalculated only when the chat history in global state actually changes.
    const recentConversations = useMemo(() => {
        const chatHistory = appStateContext?.state.chatHistory ?? [];
        return chatHistory
            .filter((c: Conversation) => c?.id)  // skip any conversations without a valid id
            .slice()                              // copy the array so the original order in global state is not changed
            .sort((a: Conversation, b: Conversation) =>
                new Date(b.date ?? 0).getTime() - new Date(a.date ?? 0).getTime() // most recent first
            )
            .slice(0, 5) // keep only the 5 newest conversations
            .map((c: Conversation) => ({
                id: c.id,
                title: c.title || "Untitled conversation", // show a friendly fallback if the conversation has no title yet
                timestamp: c.date,
            }));
    }, [appStateContext?.state.chatHistory]);

    // Called when the user clicks a conversation in the recent list.
    // Finds the full conversation object and opens it in the chat window.
    const handleRecentConversationSelect = (conversationId: string) => {
        const selected = (appStateContext?.state.chatHistory ?? []).find(
            (c: Conversation) => c.id === conversationId
        );
        if (!selected) return; // do nothing if the conversation can no longer be found in state
        appStateContext?.dispatch({ type: "UPDATE_CURRENT_CHAT", payload: selected }); // open the conversation in the main chat area
    };

    // Combine the Support Mode item (support staff only) with the standard nav items.
    // Support Mode is placed at the top of the list when present.
    const allItems = [
        ...(isSupportUser ? [{
            id: 'support-mode',
            icon: impersonationActive
                ? <FiUserCheck size={18} style={{ color: "green" }} /> // green tick = currently viewing as another user
                : <FiUserX size={18} style={{ color: "red" }} />,     // red cross = not currently impersonating
            label: t("setting.supportMode"),
            openPanel: true,
        }] : []),
        ...navItems, // append the standard nav items (Chat History, etc.)
    ];
    const handleRename = async (conversationId: string, newTitle: string) => {
        const title = newTitle.trim();

        if (!title) {
            return;
        }

        const currentConversation = chatHistory.find(
            (conversation) => conversation.id === conversationId
        );

        if (!currentConversation || currentConversation.title === title) {
            return;
        }

        try {

            const response = await historyRename(conversationId, title, country, globalId, token, appStateContext?.state.impUserCountry);

            if (!response.ok) {
                throw new Error("Failed to rename conversation.");
            }

            appStateContext?.dispatch({
                type: "UPDATE_CHAT_TITLE",
                payload: {
                    ...currentConversation,
                    title,
                } as Conversation,
            });
        } catch (error) {
            console.error("Error renaming chat history item:", error);
            toast.error("Could not rename this conversation.");
        }
    };

    const handleDelete = async (conversationId: string) => {
        const conversationToDelete = chatHistory.find(
            (conversation) => conversation.id === conversationId
        );

        if (!conversationToDelete) {
            return;
        }

        try {
            const response = await historyDelete(
                conversationToDelete.id,
                conversationToDelete.title,
                country, globalId, token, appStateContext?.state.impUserCountry
            );

            if (!response.ok) {
                throw new Error("Failed to delete conversation.");
            }

            appStateContext?.dispatch({
                type: "DELETE_CHAT_ENTRY",
                payload: conversationToDelete.id,
            });
        } catch (error) {
            console.error("Error deleting chat history item:", error);
            toast.error("Could not delete this conversation.");
        }
    };

    return (
        <>
            {allItems.map(item => {
                const isActive = activePanel === item.id && highlightActive; // true when this item's panel is currently open

                // Show recent conversations beneath the Chat History icon only when:
                // — the sidebar is expanded (wide mode), AND
                // — this is the Chat History item, AND
                // — there is at least one recent conversation to show
                const shouldShowRecentHistory =
                    item.id === 'chat-history' && expanded && recentConversations.length > 0;

                return (
                    <React.Fragment key={item.id}>
                        {/* Clickable navigation row: icon only when collapsed, icon + label when expanded */}
                        <div
                            className={mergeStyles(styles.menuItem, !expanded && styles.menuItemCollapsed, isActive && styles.menuItemActive)}
                            onClick={() => onNavItemClick(item.id)}
                        >
                            <span className={mergeStyles(styles.menuItemIcon)}>{item.icon}</span>
                            {expanded && (
                                // Label text is only shown when the sidebar is in its wide state
                                <span className={mergeStyles(styles.menuItemText, isActive && styles.menuItemTextActive)}>
                                    {item.id === 'support-mode' ? item.label : t(item.label)}
                                    {/* Support Mode label is not translated; all other labels use the translation key */}
                                </span>
                            )}
                        </div>

                        {/* Mini recent-conversations list, shown beneath the Chat History row */}
                        {shouldShowRecentHistory && (
                            <div
                                className={mergeStyles(styles.recentChatHistoryContainer)}
                                onClick={(e) => e.stopPropagation()} // prevent clicks inside the list from also triggering the nav item's click handler
                            >
                                <RecentConversationsSection
                                    recentConversations={recentConversations}
                                    activeConversationId={appStateContext?.state.currentChat?.id ?? null} // highlights the conversation currently open in the chat window
                                    onConversationSelect={handleRecentConversationSelect}                 // opens the tapped conversation
                                    onViewAll={() => {
                                        // Opens the full Chat History side panel if it is not already open
                                        if (activePanel !== 'chat-history') {
                                            onNavItemClick('chat-history');
                                        }
                                    }}
                                    onRename={handleRename}
                                    onDelete={handleDelete}
                                    actionLabels={{ renameText: t("common.edit"), deleteText: t("common.delete") }} // translated labels for the rename and delete actions on each row
                                    deleteDialogLabels={{
                                        title: t('chatHistoryListItemCell.deleteDialog.title'),
                                        bodyIntro: t('chatHistoryListItemCell.deleteDialog.subText'),
                                        subText: "",
                                        bulletItems: [],
                                        cancelText: t("common.cancel"),
                                        confirmText: t("common.confirm")
                                    }}
                                    headerText={t("chatHistoryList.recent")} // heading above the list, e.g. "Recent"
                                    footerText={t("viewAll")}                // link text at the bottom, e.g. "View all"
                                    isRtl={i18n.language === 'he' || i18n.language === 'ar'} // flip the layout direction for Hebrew and Arabic
                                    expanded={expanded}
                                />
                            </div>
                        )}
                    </React.Fragment>
                );
            })}
        </>
    );
}
